import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

import { ChatType } from "../../api/messageViews.type";

type MessageChannelContextValue = {
  chatType: ChatType;
  groupChatId: string | undefined;
  adminUserId: string | undefined;
  setChannel: (type: ChatType, groupChatId?: string) => void;
};

const MessageChannelContext = createContext<MessageChannelContextValue | null>(null);

export const MessageChannelProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [chatType, setChatType] = useState<ChatType>("ai_chat");
  const [groupChatId, setGroupChatId] = useState<string | undefined>(undefined);
  const [adminUserId, setAdminUserId] = useState<string | undefined>(undefined);

  const setChannel = useCallback((type: ChatType, id?: string) => {
    setChatType(type);
    if (type === "group_chat") {
      setGroupChatId(id);
      setAdminUserId(undefined);
    } else if (type === "all") {
      setAdminUserId(id);
      setGroupChatId(undefined);
    } else {
      setGroupChatId(undefined);
      setAdminUserId(undefined);
    }
  }, []);

  const value = useMemo(
    () => ({
      chatType,
      groupChatId,
      adminUserId,
      setChannel,
    }),
    [adminUserId, chatType, groupChatId, setChannel]
  );

  return (
    <MessageChannelContext.Provider value={value}>
      {children}
    </MessageChannelContext.Provider>
  );
};

export const useMessageChannel = () => {
  const context = useContext(MessageChannelContext);
  if (!context) {
    throw new Error("useMessageChannel must be used within a MessageChannelProvider");
  }
  return context;
};
